// [S-094] BA persona home. Two large 1-click cards into the BA's
// dominant flows:
//   • Search precedent  → routes to /precedent (gated to professional+)
//   • Browse templates  → routes to /library
//
// Plain-language framing per Brief §4: no attestation-chain detail on
// the BA-facing surface; the legal-detail toggle on TemplateDetail
// still exists for users who need it.
//
// Tier-gate state for "Search precedent": when the caller's
// accessTier is `basic`, the card renders in a disabled state with a
// "Schedule Layer 2 setup →" CTA per Brief §4. Today the CTA is a
// mailto so the BA can ask the platform team for an upgrade — when
// the self-serve provisioning flow lands it will route there.
//
// Wireframe note: per the spec this story is marked
// "Wireframe MISSING — Master Rule 1.10 pause-and-ask." The user
// authorized proceeding without the wireframe; this is a reasonable
// 1.10 interpretation of the Brief §4 description.
import { Link } from "react-router-dom";
import { useSession } from "../auth/AuthProvider";
import { Button, PageHead } from "../components";

export function BAHome(): JSX.Element {
  const session = useSession();
  // Professional or enterprise tiers see the precedent surface; basic
  // tiers see the upsell.
  const precedentEnabled =
    session?.accessTier === "professional" ||
    session?.accessTier === "enterprise";

  return (
    <>
      <PageHead
        eyebrow="Business Affairs"
        title={
          <>
            What do you need <em>right now?</em>
          </>
        }
        sub="Two paths optimized for 1-2 clicks: search past deals or browse current templates."
      />
      <section className="ba-home" data-testid="ba-home">
        <article className="ba-home__card" data-testid="ba-card-precedent">
          <header className="ba-home__card__hd">
            <h2>Search precedent</h2>
            <p>
              Look up what the studio has signed before for this kind of deal.
              Plain-language summaries, deal-count totals, and notable patterns.
            </p>
          </header>
          <footer className="ba-home__card__ft">
            {precedentEnabled ? (
              <Link to="/precedent">
                <Button variant="primary" data-testid="ba-card-precedent-cta">
                  Open precedent search →
                </Button>
              </Link>
            ) : (
              <div className="ba-home__upsell" data-testid="ba-card-precedent-upsell">
                <p>
                  Precedent not yet available on the <b>Basic</b> tier.
                </p>
                <a
                  href="mailto:platform@contractstudio.ai?subject=Layer%202%20setup"
                  data-testid="ba-card-precedent-upsell-cta"
                >
                  <Button variant="ghost">Schedule Layer 2 setup →</Button>
                </a>
              </div>
            )}
          </footer>
        </article>

        <article className="ba-home__card" data-testid="ba-card-templates">
          <header className="ba-home__card__hd">
            <h2>Browse certified templates</h2>
            <p>
              One-line synopsis for each template; the full legal chain is one
              click away on the detail page if you need it.
            </p>
          </header>
          <footer className="ba-home__card__ft">
            <Link to="/library">
              <Button variant="primary" data-testid="ba-card-templates-cta">
                Open certified templates →
              </Button>
            </Link>
          </footer>
        </article>
      </section>
    </>
  );
}
